/**
 *
 * @module submitOrderWithPaymentRequest
 *
 */

import Check from 'check-types';
import AffiliateTracking from './affiliateTracking';
import CallbackUrl from './callbackUrl';
import LineItem from './lineItem';
import OrderCampaign from './orderCampaign';
import OrderSubmissionEnvironment from './orderSubmissionEnvironment';
import RedemptionRequest from './redemptionRequest';

import InvalidArgumentException from './../exception/invalidArgumentException';

const {
    instanceStrict,
    string,
    nonEmptyObject,
    array
} = Check.assert;

/**
 *
 * @class SubmitOrderWithPaymentRequest
 *
 */
export default class SubmitOrderWithPaymentRequest {

    /**
     *
     * @param {Object} options
     * @param {AffiliateTracking} [options.affiliateTracking=undefined]
     * @param {CallbackUrl} [options.callbackUrl=undefined]
     * @param {String} options.cardSecurityCode
     * @param {String} options.emailAddress
     * @param {Array<LineItem>} options.lineItems
     * @param {Array<OrderCampaign>} options.orderCampaigns
     * @param {OrderSubmissionEnvironment} [options.orderSubmissionEnvironment=undefined]
     * @param {String} options.paymentMethodId
     * @param {SDK.Services.Commerce.RedemptionRequest} [options.redemptionRequest=undefined]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options = {}) {

        const errorMessage = {
            ctor: 'SubmitOrderWithPaymentMethodRequest(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get affiliateTracking() {
                return `${this.ctor} options.affiliateTracking should be an instance of SDK.Services.Commerce.AffiliateTracking`; // eslint-disable-line
            },
            get callbackUrl() {
                return `${this.ctor} options.callbackUrl should be an instance of SDK.Services.Commerce.CallbackUrl`;
            },
            get cardSecurityCode() {
                return `${this.ctor} options.cardSecurityCode should be a String`;
            },
            get emailAddress() {
                return `${this.ctor} options.emailAddress should be a String`;
            },
            get lineItems() {
                return `${this.ctor} needs an Array of SDK.Services.Commerce.LineItem`;
            },
            get orderCampaigns() {
                return `${this.ctor} needs an Array of SDK.Services.Commerce.OrderCampaign`;
            },
            get orderSubmissionEnvironment() {
                return `${this.ctor} options.orderSubmissionEnvironment should be an instance of SDK.Services.Commerce.OrderSubmissionEnvironment`; // eslint-disable-line
            },
            get paymentMethodId() {
                return `${this.ctor} options.paymentMethodId should be a String`;
            },
            get redemptionRequest() {
                return `${this.ctor} needs SDK.Services.Commerce.RedemptionRequest instance`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { affiliateTracking, callbackUrl, cardSecurityCode, emailAddress, lineItems } = options;
        const { orderCampaigns, orderSubmissionEnvironment, paymentMethodId, redemptionRequest } = options;

        if (Check.assigned(affiliateTracking)) {
            instanceStrict(
                affiliateTracking,
                AffiliateTracking,
                errorMessage.affiliateTracking,
                InvalidArgumentException
            );
        }

        if (Check.assigned(callbackUrl)) {
            instanceStrict(callbackUrl, CallbackUrl, errorMessage.callbackUrl, InvalidArgumentException);
        }

        string(cardSecurityCode, errorMessage.cardSecurityCode, InvalidArgumentException);
        string(emailAddress, errorMessage.emailAddress, InvalidArgumentException);
        array.of.instanceStrict(lineItems, LineItem, errorMessage.lineItem, InvalidArgumentException);

        array.of.instanceStrict(
            orderCampaigns,
            OrderCampaign,
            errorMessage.orderCampaigns,
            InvalidArgumentException
        );

        if (Check.assigned(orderSubmissionEnvironment)) {
            instanceStrict(
                orderSubmissionEnvironment,
                OrderSubmissionEnvironment,
                errorMessage.orderSubmissionEnvironment,
                InvalidArgumentException
            );
        }

        string(paymentMethodId, errorMessage.paymentMethodId, InvalidArgumentException);

        if (Check.assigned(redemptionRequest)) {
            instanceStrict(redemptionRequest, RedemptionRequest, errorMessage.redemptionRequest, InvalidArgumentException); // eslint-disable-line
        }

        /**
         *
         * @access public
         * @type {CallbackUrl}
         *
         */
        this.callbackUrl = callbackUrl;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.cardSecurityCode = cardSecurityCode;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.emailAddress = emailAddress;

        /**
         *
         * @access public
         * @type {Array<LineItem>}
         *
         */
        this.lineItems = lineItems;

        /**
         *
         * @access public
         * @type {Array<OrderCampaign>}
         *
         */
        this.orderCampaigns = orderCampaigns;

        /**
         *
         * @access public
         * @type {OrderSubmissionEnvironment}
         *
         */
        this.orderSubmissionEnvironment = orderSubmissionEnvironment;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.paymentMethodId = paymentMethodId;

    }

}



// WEBPACK FOOTER //
// ./src/services/commerce/submitOrderWithPaymentMethodRequest.js